import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { FileText, ImageIcon, Loader2, Paperclip, X } from 'lucide-react'
import { type DragEvent, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ACCEPTED_ATTACHMENT_TYPES, type BugAttachments, MAX_ATTACHMENT_MB, MAX_ATTACHMENTS } from './useBugAttachments'

type Props = {
  readonly attachments: BugAttachments
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Attachment picker for the bug form: click, drag-and-drop, or paste a screenshot. */
export function BugAttachmentsField({ attachments }: Props) {
  const { t } = useTranslation()
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const { items, addFiles, remove, isFull } = attachments

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragging(false)
    if (!isFull) addFiles(Array.from(e.dataTransfer.files))
  }

  return (
    <div className='flex flex-col gap-1.5'>
      <Label htmlFor={inputId}>{t('reportBug.attachments.label')}</Label>

      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag events only; the
          keyboard path is the labelled file input and the button below. */}
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
          isDragging ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white'
        }`}
      >
        <Paperclip className='mx-auto h-6 w-6 text-gray-400' />
        <p className='mt-1 text-gray-600 text-sm'>{t('reportBug.attachments.hint')}</p>
        <p className='text-gray-400 text-xs'>
          {t('reportBug.attachments.limits', { max: MAX_ATTACHMENTS, size: MAX_ATTACHMENT_MB })}
        </p>
        <input
          id={inputId}
          ref={inputRef}
          type='file'
          multiple
          accept={ACCEPTED_ATTACHMENT_TYPES}
          className='sr-only'
          disabled={isFull}
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []))
            // Let the same file be picked again after a removal.
            e.target.value = ''
          }}
        />
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='mt-3'
          disabled={isFull}
          onClick={() => inputRef.current?.click()}
        >
          {t('reportBug.attachments.choose')}
        </Button>
      </div>

      {items.length > 0 && (
        <ul className='flex flex-col gap-1.5'>
          {items.map((item) => (
            <li key={item.localId} className='flex items-center gap-2 rounded-md border bg-white px-2 py-1.5'>
              {item.previewUrl ? (
                <img src={item.previewUrl} alt='' className='h-8 w-8 shrink-0 rounded object-cover' />
              ) : (
                <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-100'>
                  {item.name.match(/\.(png|jpe?g|gif|webp)$/i) ? (
                    <ImageIcon className='h-4 w-4 text-gray-500' />
                  ) : (
                    <FileText className='h-4 w-4 text-gray-500' />
                  )}
                </span>
              )}

              <span className='min-w-0 flex-1'>
                <span className='block truncate text-sm'>{item.name}</span>
                <span className={`block text-xs ${item.status === 'error' ? 'text-destructive' : 'text-gray-500'}`}>
                  {item.status === 'error' ? item.error : formatSize(item.size)}
                </span>
              </span>

              {item.status === 'uploading' && <Loader2 className='h-4 w-4 shrink-0 animate-spin text-gray-400' />}

              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-7 w-7 shrink-0'
                onClick={() => remove(item.localId)}
                aria-label={t('reportBug.attachments.remove', { name: item.name })}
              >
                <X className='h-4 w-4' />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
